import React, { Fragment, type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { flexRender, useTable } from '@tanstack/react-table';
import {
    dataTableFeatures,
    type ColumnDef,
    type DataTableColumnMeta,
    type ExpandedState,
    type Row,
    type RowData,
    type SortingState,
    type VisibilityState
} from './dataTableConfig';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import FormInput from './FormInput.tsx';

export interface DataTableProps<TData extends RowData> {
    data: TData[];
    columns: ColumnDef<TData>[];
    search?: boolean;
    sort?: boolean;
    pagination?: {
        enabled: boolean;
        pageSize: number;
        /** The caller supplies one server page and owns navigation. */
        server?: { pageIndex: number; rowCount: number; onPageChange: (pageIndex: number) => void };
    };
    striped?: boolean;
    allowOverflow?: boolean; // Controls whether the table can overflow with scrollbar
    onRowClick?: (row: TData) => void; // Callback for when a row is clicked
    /**
     * If true, the table width shrinks to fit its content (w-auto) rather than stretching to the parent width.
     * Useful for nested tables to avoid unnecessary overflow when only a few columns are visible.
     */
    shrinkToContent?: boolean;
    /** When true, sorting is handled externally (server-side). TanStack Table skips client-side sort. */
    manualSorting?: boolean;
    /** Controlled sorting state (used with manualSorting). */
    sorting?: SortingState;
    /** Callback when user clicks a column header (used with manualSorting). */
    onSortingChange?: (sorting: SortingState) => void;
    /** Controlled column visibility state. When provided, the consumer owns column show/hide. */
    columnVisibility?: VisibilityState;
    /** Callback invoked when column visibility changes. */
    onColumnVisibilityChange?: (visibility: VisibilityState) => void;
    /** Detail panel rendered beneath an expanded row. Providing it prepends an expander column. */
    renderSubComponent?: (props: { row: Row<TData> }) => ReactNode;
    /** Restricts which rows can expand. Defaults to every row when `renderSubComponent` is set. */
    getRowCanExpand?: (row: Row<TData>) => boolean;
    /** Stable row identity; keeps expansion state attached to the right row when `data` changes. */
    getRowId?: (row: TData) => string;
    /**
     * Caps the table at this CSS height and scrolls it internally, which keeps the header row pinned
     * and the horizontal scrollbar on screen. Without it the page scrolls, and a wide table's
     * scrollbar sits under its last row.
     */
    maxHeight?: string;
}

interface StickyPlacement {
    side: 'left' | 'right';
    offset: number;
    /** The pinned column that borders the scrolling columns, which carries the edge shadow. */
    bordersScrollingColumns: boolean;
}

interface HorizontalScrollState {
    hasHiddenStart: boolean;
    hasHiddenEnd: boolean;
    viewportWidth: number;
    contentWidth: number;
}

const INITIAL_SCROLL_STATE: HorizontalScrollState = { hasHiddenStart: false, hasHiddenEnd: false, viewportWidth: 0, contentWidth: 0 };

/** Sub-pixel slack so fractional scroll positions at either end do not read as hidden columns. */
const SCROLL_EDGE_TOLERANCE_PX = 1;

/** Room kept for the scrolling columns; narrower than this, pinned columns would cover the table, so nothing is pinned. */
const MIN_UNPINNED_WIDTH_PX = 240;

const PINNED_LEFT_EDGE_SHADOW = 'shadow-[8px_0_10px_-4px_rgba(0,0,0,0.75)]';
const PINNED_RIGHT_EDGE_SHADOW = 'shadow-[-8px_0_10px_-4px_rgba(0,0,0,0.75)]';

const getColumnMeta = (columnDef: object): DataTableColumnMeta | undefined => (columnDef as { meta?: DataTableColumnMeta }).meta;

/**
 * Resolves each pinned column's edge offset. Columns without an explicit `stickyOffset` stack against
 * the pinned columns between them and their edge, using rendered widths once measured and `size`
 * until then, so hiding or resizing a pinned column never leaves a gap or an overlap.
 */
const computeStickyPlacements = (
    columns: readonly { id: string; columnDef: { size?: number } }[],
    measuredWidths: ReadonlyMap<string, number>
): Map<string, StickyPlacement> => {
    const placements = new Map<string, StickyPlacement>();

    const placeSide = (orderedFromEdge: readonly { id: string; columnDef: { size?: number } }[], side: 'left' | 'right') => {
        let nextOffset = 0;
        let innermostPlacement: StickyPlacement | null = null;

        for (const column of orderedFromEdge) {
            const meta = getColumnMeta(column.columnDef);

            if (meta?.sticky !== side) continue;

            const placement: StickyPlacement = { side, offset: meta.stickyOffset ?? nextOffset, bordersScrollingColumns: false };
            placements.set(column.id, placement);
            nextOffset = placement.offset + (measuredWidths.get(column.id) ?? column.columnDef.size ?? 0);
            innermostPlacement = placement;
        }

        if (innermostPlacement) innermostPlacement.bordersScrollingColumns = true;
    };

    placeSide(columns, 'left');
    placeSide([...columns].reverse(), 'right');

    return placements;
};

const widthMapsMatch = (left: ReadonlyMap<string, number>, right: ReadonlyMap<string, number>): boolean =>
    left.size === right.size && [...left].every(([columnId, width]) => right.get(columnId) === width);

// Universal sort function to handle numbers, currency strings ("$1,234.56"), dates, and fallback to string
function universalSort<TData extends RowData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): number {
    const parseValue = (val: unknown) => {
        if (typeof val === 'number') return { type: 'number', value: val };

        if (typeof val === 'string') {
            // Check if it's a datetime string (YYYY-MM-DD or YYYY-MM-DD HH:MM:SS format)
            const dateTimeRegex = /^\d{4}-\d{2}-\d{2}(\s\d{2}:\d{2}(:\d{2})?)?$/;

            if (dateTimeRegex.test(val)) {
                const date = new Date(val);

                if (!isNaN(date.getTime())) {
                    return { type: 'date', value: date.getTime() };
                }
            }

            // Check if it's a number or currency
            const num = parseFloat(val.replace(/[^0-9.-]+/g, ''));
            if (!isNaN(num)) return { type: 'number', value: num };
        }

        return null;
    };

    const aRaw = rowA.getValue(columnId);
    const bRaw = rowB.getValue(columnId);

    const aParsed = parseValue(aRaw);
    const bParsed = parseValue(bRaw);

    // If both can be parsed as numbers or dates, compare them
    if (aParsed !== null && bParsed !== null) {
        return aParsed.value - bParsed.value;
    }

    // Fallback to string comparison
    return String(aRaw).localeCompare(String(bRaw));
}

export function DataTable<TData extends RowData>({
    data,
    columns,
    search = true,
    sort = true,
    pagination = { enabled: false, pageSize: 20 },
    striped = true,
    allowOverflow = true,
    onRowClick,
    shrinkToContent = false,
    manualSorting: manualSortingProp = false,
    sorting: externalSorting,
    onSortingChange: externalOnSortingChange,
    columnVisibility: externalColumnVisibility,
    onColumnVisibilityChange: externalOnColumnVisibilityChange,
    renderSubComponent,
    getRowCanExpand,
    getRowId,
    maxHeight
}: DataTableProps<TData>) {
    const [globalFilter, setGlobalFilter] = useState('');
    const [internalSorting, setInternalSorting] = useState<SortingState>([]);
    const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({});
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const isManualSorting = manualSortingProp && externalSorting !== undefined && externalOnSortingChange !== undefined;
    const activeSorting = isManualSorting ? externalSorting : internalSorting;
    const isControlledVisibility = externalColumnVisibility !== undefined && externalOnColumnVisibilityChange !== undefined;
    const activeColumnVisibility = isControlledVisibility ? externalColumnVisibility : internalColumnVisibility;

    const handleSortingChange = isManualSorting
        ? (updater: SortingState | ((prev: SortingState) => SortingState)) => {
              const next = typeof updater === 'function' ? updater(externalSorting) : updater;
              externalOnSortingChange(next);
          }
        : setInternalSorting;

    const handleColumnVisibilityChange = isControlledVisibility
        ? (updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
              const next = typeof updater === 'function' ? updater(externalColumnVisibility) : updater;
              externalOnColumnVisibilityChange(next);
          }
        : setInternalColumnVisibility;

    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: pagination?.enabled ? pagination.pageSize : 20
    });
    const activePagination = pagination.server
        ? { pageIndex: pagination.server.pageIndex, pageSize: pagination.pageSize }
        : paginationState;
    const handlePaginationChange = (updater: typeof paginationState | ((previous: typeof paginationState) => typeof paginationState)) => {
        const next = typeof updater === 'function' ? updater(activePagination) : updater;
        if (pagination.server) pagination.server.onPageChange(next.pageIndex);
        else setPaginationState(next);
    };

    // The expander is prepended rather than left to the caller so sticky columns keep their edge offsets.
    // It joins the left-pinned group when there is one, or pinned columns would slide over it.
    const hasLeftPinnedColumn = columns.some((column) => getColumnMeta(column)?.sticky === 'left');
    const tableColumns = renderSubComponent
        ? [
              {
                  id: 'expander',
                  header: () => null,
                  enableSorting: false,
                  meta: hasLeftPinnedColumn ? ({ sticky: 'left' } satisfies DataTableColumnMeta) : undefined,
                  cell: ({ row }: { row: Row<TData> }) =>
                      row.getCanExpand() ? (
                          <button
                              aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
                              className="rounded p-1 transition-colors hover:bg-zinc-700"
                              type="button"
                              onClick={(event) => {
                                  event.stopPropagation();
                                  row.toggleExpanded();
                              }}
                          >
                              {row.getIsExpanded() ? (
                                  <FaChevronDown className="text-zinc-400" />
                              ) : (
                                  <FaChevronRight className="text-zinc-400" />
                              )}
                          </button>
                      ) : null,
                  size: 40,
                  minSize: 40,
                  maxSize: 40
              },
              ...columns
          ]
        : columns;

    const table = useTable({
        features: dataTableFeatures,
        data,
        columns: tableColumns,
        enableSorting: sort,
        manualSorting: !sort || isManualSorting,
        manualPagination: !pagination.enabled || Boolean(pagination.server),
        ...(pagination.server ? { rowCount: pagination.server.rowCount, autoResetPageIndex: false } : {}),
        state: {
            ...(search ? { globalFilter } : {}),
            ...(sort ? { sorting: activeSorting } : {}),
            ...(pagination?.enabled ? { pagination: activePagination } : {}),
            columnVisibility: activeColumnVisibility,
            expanded
        },
        onGlobalFilterChange: search ? setGlobalFilter : undefined,
        onSortingChange: sort ? handleSortingChange : undefined,
        onPaginationChange: pagination?.enabled ? handlePaginationChange : undefined,
        onColumnVisibilityChange: handleColumnVisibilityChange,
        onExpandedChange: setExpanded,
        getRowCanExpand: getRowCanExpand ?? (() => Boolean(renderSubComponent)),
        ...(getRowId ? { getRowId } : {}),
        defaultColumn: {
            sortFn: universalSort,
            minSize: 50,
            size: 150,
            maxSize: Number.MAX_SAFE_INTEGER
        },
        columnResizeMode: 'onChange',
        // Case-insensitive multi-term search with AND/OR/NOT
        // Syntax: space = AND, 'a|b' = OR, '-token' = NOT
        globalFilterFn: (row, _columnId, value) => {
            const raw = String(value).trim().toLowerCase();
            if (!raw) return true;

            const haystack = row
                .getAllCells()
                .map((cell) => (cell.getValue() != null ? String(cell.getValue()).toLowerCase() : ''))
                .join(' ');

            const tokens = raw.split(/\s+/);

            // Every token must be satisfied (AND)
            for (const tok of tokens) {
                if (!tok) continue;
                const isNeg = tok.startsWith('-');
                const body = isNeg ? tok.slice(1) : tok;
                const alts = body.split('|').filter(Boolean);

                if (alts.length === 0) continue;

                if (isNeg) {
                    // NOT: none of the alternatives may appear
                    const anyPresent = alts.some((alt) => haystack.includes(alt));
                    if (anyPresent) return false;
                } else {
                    // Positive: at least one alternative must appear
                    const anyPresent = alts.some((alt) => haystack.includes(alt));
                    if (!anyPresent) return false;
                }
            }

            return true;
        }
    });

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const floatingScrollbarRef = useRef<HTMLDivElement>(null);
    const tableElementRef = useRef<HTMLTableElement>(null);
    const pinnedHeaderCellsRef = useRef(new Map<string, HTMLTableCellElement>());
    const [pinnedColumnWidths, setPinnedColumnWidths] = useState<ReadonlyMap<string, number>>(() => new Map());
    const [scrollState, setScrollState] = useState<HorizontalScrollState>(INITIAL_SCROLL_STATE);

    const visibleLeafColumns = table.getVisibleLeafColumns();
    /*
     * Judged on declared sizes rather than measured widths: those are only measured while pinned, so
     * deciding on them could flip pinning on and off at the threshold.
     */
    const pinnableSizeTotal = visibleLeafColumns.reduce(
        (total, column) => total + (getColumnMeta(column.columnDef)?.sticky ? (column.columnDef.size ?? 0) : 0),
        0
    );
    const hasRoomToPin = scrollState.viewportWidth === 0 || pinnableSizeTotal + MIN_UNPINNED_WIDTH_PX <= scrollState.viewportWidth;
    const stickyPlacements = hasRoomToPin
        ? computeStickyPlacements(visibleLeafColumns, pinnedColumnWidths)
        : new Map<string, StickyPlacement>();
    const pinnedColumnIdsKey = [...stickyPlacements.keys()].join('|');
    const pinnedPlacements = [...stickyPlacements.values()];
    const hasLeftPinnedGroup = pinnedPlacements.some((placement) => placement.side === 'left');
    const hasRightPinnedGroup = pinnedPlacements.some((placement) => placement.side === 'right');
    const hasStickyHeader = allowOverflow && maxHeight !== undefined;

    // Pinned offsets depend on the rendered widths of the pinned columns, which auto table layout
    // only settles after paint (padding, content, and column sizing all feed in).
    useLayoutEffect(() => {
        const pinnedHeaderCells = pinnedHeaderCellsRef.current;

        if (pinnedHeaderCells.size === 0) {
            setPinnedColumnWidths((previousWidths) => (previousWidths.size === 0 ? previousWidths : new Map()));
            return;
        }

        const columnIdByCell = new Map<Element, string>([...pinnedHeaderCells].map(([columnId, cell]) => [cell, columnId]));

        // borderBoxSize is in the cell's own CSS pixels, so an ancestor's transform or zoom cannot skew the offsets.
        const observer = new ResizeObserver((entries) => {
            setPinnedColumnWidths((previousWidths) => {
                const nextWidths = new Map(previousWidths);

                for (const entry of entries) {
                    const columnId = columnIdByCell.get(entry.target);
                    const [borderBoxSize] = entry.borderBoxSize;

                    if (columnId !== undefined && borderBoxSize) nextWidths.set(columnId, borderBoxSize.inlineSize);
                }

                return widthMapsMatch(previousWidths, nextWidths) ? previousWidths : nextWidths;
            });
        });
        pinnedHeaderCells.forEach((cell) => observer.observe(cell));

        return () => observer.disconnect();
    }, [pinnedColumnIdsKey]);

    const updateScrollState = useCallback(() => {
        const container = scrollContainerRef.current;

        if (!container) return;

        const maxScrollLeft = container.scrollWidth - container.clientWidth;
        const nextState: HorizontalScrollState = {
            hasHiddenStart: container.scrollLeft > SCROLL_EDGE_TOLERANCE_PX,
            hasHiddenEnd: container.scrollLeft < maxScrollLeft - SCROLL_EDGE_TOLERANCE_PX,
            viewportWidth: container.clientWidth,
            contentWidth: container.scrollWidth
        };

        setScrollState((previousState) =>
            previousState.hasHiddenStart === nextState.hasHiddenStart &&
            previousState.hasHiddenEnd === nextState.hasHiddenEnd &&
            previousState.viewportWidth === nextState.viewportWidth &&
            previousState.contentWidth === nextState.contentWidth
                ? previousState
                : nextState
        );
    }, []);

    const hasHorizontalOverflow = allowOverflow && scrollState.contentWidth - scrollState.viewportWidth > SCROLL_EDGE_TOLERANCE_PX;

    /*
     * The table and the floating scrollbar mirror each other's horizontal position. Each only writes
     * when the other differs, so the scroll event the write triggers ends the exchange.
     */
    const handleContainerScroll = useCallback(() => {
        updateScrollState();

        const container = scrollContainerRef.current;
        const floatingScrollbar = floatingScrollbarRef.current;

        if (container && floatingScrollbar && Math.abs(floatingScrollbar.scrollLeft - container.scrollLeft) >= 1) {
            floatingScrollbar.scrollLeft = container.scrollLeft;
        }
    }, [updateScrollState]);

    const handleFloatingScrollbarScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        const floatingScrollbar = floatingScrollbarRef.current;

        if (container && floatingScrollbar && Math.abs(container.scrollLeft - floatingScrollbar.scrollLeft) >= 1) {
            container.scrollLeft = floatingScrollbar.scrollLeft;
        }
    }, []);

    // A floating scrollbar that appears after the table was already scrolled starts at the table's position.
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        const floatingScrollbar = floatingScrollbarRef.current;

        if (hasHorizontalOverflow && container && floatingScrollbar) floatingScrollbar.scrollLeft = container.scrollLeft;
    }, [hasHorizontalOverflow]);

    // The container resizes with the viewport and the table with its rows and columns; either can
    // reveal or hide columns without a scroll event.
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        const tableElement = tableElementRef.current;

        if (!allowOverflow || !container || !tableElement) return;

        // Measured before the first paint too, so a narrow table never flashes its columns pinned.
        updateScrollState();

        const observer = new ResizeObserver(updateScrollState);
        observer.observe(container);
        observer.observe(tableElement);

        return () => observer.disconnect();
    }, [allowOverflow, updateScrollState]);

    const getPinnedCellPresentation = (columnId: string, cellKind: 'header' | 'body') => {
        const placement = stickyPlacements.get(columnId);

        if (!placement) return { isPinned: false, className: '', style: {} satisfies React.CSSProperties };

        const hasHiddenColumnsBeside = placement.side === 'left' ? scrollState.hasHiddenStart : scrollState.hasHiddenEnd;
        const edgeShadow =
            placement.bordersScrollingColumns && hasHiddenColumnsBeside
                ? placement.side === 'left'
                    ? PINNED_LEFT_EDGE_SHADOW
                    : PINNED_RIGHT_EDGE_SHADOW
                : '';
        // Striped rows paint their own background, so pinned body cells inherit it to keep the stripe and hover.
        const background = cellKind === 'header' ? 'bg-[#131416]' : striped ? 'bg-inherit' : 'bg-[#222222]';
        const layer = cellKind === 'header' ? (hasStickyHeader ? 'z-30' : 'z-20') : 'z-10';

        return {
            isPinned: true,
            className: `sticky ${layer} ${background} ${edgeShadow}`,
            style: (placement.side === 'left' ? { left: placement.offset } : { right: placement.offset }) satisfies React.CSSProperties
        };
    };

    const registerPinnedHeaderCell = (columnId: string) => (cell: HTMLTableCellElement | null) => {
        if (cell && stickyPlacements.has(columnId)) {
            pinnedHeaderCellsRef.current.set(columnId, cell);
        } else {
            pinnedHeaderCellsRef.current.delete(columnId);
        }
    };

    return (
        <div className="w-full max-w-full">
            {search && (
                <div className="mb-2 sm:mb-3 md:mb-4">
                    <FormInput
                        className="w-full md:w-64"
                        placeholder="Search..."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                    <div className="mt-1 text-xs text-zinc-500">
                        Tips: space = AND, use &quot;|&quot; for OR, prefix with &quot;-&quot; to exclude. Example: spy candle|-wick
                        -futures
                    </div>
                </div>
            )}

            <div className={`relative ${shrinkToContent ? 'inline-block max-w-full align-top' : 'block'}`}>
                {/* Pinned columns carry their own edge shadow; an unpinned edge gets a fade instead. */}
                {scrollState.hasHiddenStart && !hasLeftPinnedGroup && (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 left-0 z-40 w-6 bg-linear-to-r from-black/60 to-transparent"
                    />
                )}
                {scrollState.hasHiddenEnd && !hasRightPinnedGroup && (
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-y-0 right-0 z-40 w-6 bg-linear-to-l from-black/60 to-transparent"
                    />
                )}
                <div
                    className={`block ${
                        allowOverflow ? `data-table-scroll ${hasStickyHeader ? 'overflow-auto' : 'overflow-x-auto'}` : 'overflow-hidden'
                    }`}
                    ref={scrollContainerRef}
                    style={hasStickyHeader ? { maxHeight } : undefined}
                    onScroll={allowOverflow ? handleContainerScroll : undefined}
                >
                    <table
                        className={`${shrinkToContent ? 'w-auto' : 'w-full'} divide-y divide-zinc-800 ${
                            striped ? 'bg-[#222222] [&>tbody>*:nth-child(odd)]:bg-[#2a2a2a]' : ''
                        }`}
                        ref={tableElementRef}
                    >
                        <thead className="bg-[#131416]">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={`header-group-${headerGroup.id}`}>
                                    {headerGroup.headers.map((header) => {
                                        const canSort = header.column.getCanSort();
                                        const meta = getColumnMeta(header.column.columnDef);
                                        const pinned = getPinnedCellPresentation(header.column.id, 'header');

                                        return (
                                            <th
                                                className={`px-2 py-1 sm:px-3 sm:py-2 md:px-3 md:py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider overflow-hidden bg-[#131416] ${
                                                    hasStickyHeader ? 'sticky top-0' : ''
                                                } ${hasStickyHeader && !pinned.isPinned ? 'z-20' : ''} ${
                                                    canSort ? 'cursor-pointer hover:bg-[#252525]' : ''
                                                } ${meta?.headerClassName ?? ''} ${pinned.className}`}
                                                data-column-id={header.column.id}
                                                key={`header-${header.id}`}
                                                ref={registerPinnedHeaderCell(header.column.id)}
                                                style={{
                                                    minWidth: header.column.columnDef.minSize
                                                        ? `${header.column.columnDef.minSize}px`
                                                        : undefined,
                                                    maxWidth: header.column.columnDef.maxSize
                                                        ? `${header.column.columnDef.maxSize}px`
                                                        : undefined,
                                                    width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : undefined,
                                                    ...pinned.style
                                                }}
                                                onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                            >
                                                <div className="flex items-center">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {canSort && (
                                                        <span className="ml-1">
                                                            {header.column.getIsSorted() === 'asc' ? (
                                                                <svg
                                                                    className="h-4 w-4 inline"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        d="M5 15l7-7 7 7"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                    />
                                                                </svg>
                                                            ) : header.column.getIsSorted() === 'desc' ? (
                                                                <svg
                                                                    className="h-4 w-4 inline"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        d="M19 9l-7 7-7-7"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                    />
                                                                </svg>
                                                            ) : (
                                                                <svg
                                                                    className="h-4 w-4 inline text-zinc-400"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    viewBox="0 0 24 24"
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                >
                                                                    <path
                                                                        d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                        strokeWidth="2"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {table.getRowModel().rows.map((row) => (
                                <Fragment key={`row-${row.id}`}>
                                    <tr
                                        className={`${striped ? 'bg-[#222222]' : ''} hover:bg-[#333333] ${onRowClick ? 'cursor-pointer' : ''}`}
                                        onClick={() => onRowClick?.(row.original)}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            const meta = getColumnMeta(cell.column.columnDef);
                                            const pinned = getPinnedCellPresentation(cell.column.id, 'body');

                                            return (
                                                <td
                                                    className={`px-3 py-3 break-words overflow-hidden text-zinc-50 ${meta?.className ?? ''} ${
                                                        pinned.className
                                                    }`}
                                                    data-column-id={cell.column.id}
                                                    key={`cell-${cell.id}`}
                                                    style={{
                                                        minWidth: cell.column.columnDef.minSize
                                                            ? `${cell.column.columnDef.minSize}px`
                                                            : undefined,
                                                        maxWidth: cell.column.columnDef.maxSize
                                                            ? `${cell.column.columnDef.maxSize}px`
                                                            : undefined,
                                                        width: cell.column.columnDef.size ? `${cell.column.columnDef.size}px` : undefined,
                                                        ...pinned.style
                                                    }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {row.getIsExpanded() && renderSubComponent ? (
                                        <tr>
                                            <td className="bg-zinc-900 p-0" colSpan={row.getVisibleCells().length}>
                                                {/*
                                                 * Held to the visible width and pinned left, so the detail panel stays
                                                 * in view instead of spanning (and scrolling with) the full table width.
                                                 */}
                                                <div
                                                    className={`border-l-4 border-zinc-700 ${allowOverflow ? 'sticky left-0' : ''}`}
                                                    style={
                                                        allowOverflow && scrollState.viewportWidth > 0
                                                            ? { width: scrollState.viewportWidth }
                                                            : undefined
                                                    }
                                                >
                                                    {renderSubComponent({ row })}
                                                </div>
                                            </td>
                                        </tr>
                                    ) : null}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/*
                 * The table's horizontal scrollbar, drawn outside it and stuck to the bottom of the window
                 * while the table's own bottom edge is out of view. Otherwise it sits under the last row (or a
                 * viewport below the fold, for a height-capped table) where nobody finds it. Pointer-only: the
                 * table itself stays keyboard and trackpad scrollable, so assistive tech skips this copy.
                 */}
                {hasHorizontalOverflow && (
                    <div
                        aria-hidden="true"
                        className="data-table-scrollbar sticky bottom-0 z-40 overflow-x-auto overflow-y-hidden bg-[#18181b]"
                        ref={floatingScrollbarRef}
                        style={{ width: scrollState.viewportWidth }}
                        tabIndex={-1}
                        onScroll={handleFloatingScrollbarScroll}
                    >
                        <div className="h-px" style={{ width: scrollState.contentWidth }} />
                    </div>
                )}
            </div>

            {pagination.enabled && table.getPageCount() > 1 && (
                <div className="flex items-center justify-center mt-4 gap-3">
                    <button
                        aria-label="Previous page"
                        className={`flex items-center justify-center w-8 h-8 rounded border border-zinc-800 ${
                            !table.getCanPreviousPage()
                                ? 'bg-[#222222] text-zinc-500 cursor-not-allowed opacity-50'
                                : 'bg-[#222222] text-zinc-300 hover:bg-[#333333]'
                        }`}
                        disabled={!table.getCanPreviousPage()}
                        onClick={() => table.previousPage()}
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                        </svg>
                    </button>
                    <span className="text-sm text-zinc-300">
                        Page {table.state.pagination.pageIndex + 1} of {table.getPageCount()}
                    </span>
                    <button
                        aria-label="Next page"
                        className={`flex items-center justify-center w-8 h-8 rounded border border-zinc-800 ${
                            !table.getCanNextPage()
                                ? 'bg-[#222222] text-zinc-500 cursor-not-allowed opacity-50'
                                : 'bg-[#222222] text-zinc-300 hover:bg-[#333333]'
                        }`}
                        disabled={!table.getCanNextPage()}
                        onClick={() => table.nextPage()}
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
}

export default DataTable;
